<template>
  <generic-modal :title="modal_title" ref="generic_modal">
    <div class="columns">
      <div class="column">
        <div class="field">
          <label class="label">{{ $t("name") }}</label>
          <div class="control">
            <input
              class="input"
              type="text"
              v-model="meta.name"
              :placeholder="$t('name')"
            />
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("description") }}</label>
          <div class="control">
            <textarea
              class="textarea"
              v-model="meta.description"
              :placeholder="$t('description')"
              rows="3"
            ></textarea>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("color") }}</label>
          <div class="control">
            <input
              class="input"
              type="color"
              v-model="meta.color"
              style="width: 100px; height: 40px"
            />
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("sort_order") }}</label>
          <div class="control">
            <input
              class="input"
              type="number"
              v-model="meta.sort_order"
              :placeholder="$t('sort_order')"
              min="0"
            />
          </div>
          <p class="help">{{ $t("sort_order_help") }}</p>
        </div>
      </div>

      <div class="column">
        <div class="field">
          <label class="label">{{ $t("status_type") }}</label>
          <div class="control">
            <label class="radio">
              <input
                type="radio"
                name="status_type"
                value="open"
                v-model="status_type"
              />
              {{ $t("open_in_progress") }}
            </label>
          </div>
          <div class="control">
            <label class="radio">
              <input
                type="radio"
                name="status_type"
                value="resolved"
                v-model="status_type"
              />
              {{ $t("resolved") }}
            </label>
          </div>
          <div class="control">
            <label class="radio">
              <input
                type="radio"
                name="status_type"
                value="closed"
                v-model="status_type"
              />
              {{ $t("closed") }}
            </label>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("notifications") }}</label>
          <div class="control">
            <label class="checkbox">
              <input type="checkbox" v-model="meta.send_email_notification" />
              {{ $t("send_email_notification") }}
            </label>
          </div>
          <div class="control">
            <label class="checkbox">
              <input type="checkbox" v-model="meta.send_sms_notification" />
              {{ $t("send_sms_notification") }}
            </label>
          </div>
        </div>

        <div class="field">
          <div class="control">
            <label class="checkbox">
              <input type="checkbox" v-model="meta.is_active" />
              {{ $t("is_active") }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Manejo de errores -->
    <div class="notification is-danger mt-5" v-if="error">
      <p v-html="error"></p>
    </div>

    <!-- Footer con botones -->
    <template v-slot:footer>
      <button
        class="button is-primary is-rounded"
        @click="save"
        :disabled="is_form_invalid"
      >
        <span class="icon"><i class="fa fa-save"></i></span>
        <span>{{ $t("save") }}</span>
      </button>
      <button
        class="button is-danger is-rounded"
        @click="remove"
        v-if="meta.uuid"
      >
        <span class="icon"><i class="fa fa-trash"></i></span>
        <span>{{ $t("delete") }}</span>
      </button>
      <button class="button is-rounded" @click="close_modal">
        <span class="icon"><i class="fa fa-close"></i></span>
        <span>{{ $t("cancel") }}</span>
      </button>
    </template>
  </generic-modal>
</template>

<script>
import {
  put,
  post,
  del,
  get,
  generate_error_message_text,
  confirm_deletion_dialog,
} from "@/utils";
import GenericModal from "@/components/generic_modal/Index.vue";

export default {
  name: "StatusModal",
  components: { GenericModal },
  data() {
    return {
      meta: {},
      status_type: "open",
      error: "",
    };
  },
  computed: {
    is_form_invalid() {
      return !this.meta.name;
    },
    modal_title() {
      return this.meta.uuid ? this.$t("edit_status") : this.$t("create_status");
    },
  },
  watch: {
    status_type(newValue) {
      // Actualizar los campos basado en el tipo de status
      this.meta.is_closed_status = newValue === "closed";
      this.meta.is_resolved_status = newValue === "resolved";
    },
  },
  methods: {
    close_modal() {
      this.$refs.generic_modal.close_modal();
      this.set_initial_state();
    },

    show_modal(uuid = null) {
      this.set_initial_state();

      if (uuid) {
        get("/helpdesk/status", { uuid })
          .then((status) => {
            this.meta = status;

            // Determinar el tipo de status
            if (status.is_closed_status) {
              this.status_type = "closed";
            } else if (status.is_resolved_status) {
              this.status_type = "resolved";
            } else {
              this.status_type = "open";
            }

            this.$refs.generic_modal.show_modal();
          })
          .catch((error) => {
            this.error = generate_error_message_text(error);
          });
      } else {
        this.$refs.generic_modal.show_modal();
      }
    },

    save() {
      const isEdit = !!this.meta.uuid;
      const method = isEdit ? post : put;
      const endpoint = "/helpdesk/status";

      method(endpoint, this.meta)
        .then(() => {
          this.$emit("success");
          this.$refs.generic_modal.close_modal();
        })
        .catch((error) => {
          this.error = generate_error_message_text(error);
        });
    },

    remove() {
      confirm_deletion_dialog(this.$t("are_you_sure_delete"), this, () => {
        del("/helpdesk/status", { uuid: this.meta.uuid })
          .then(() => {
            this.$emit("success");
            this.$refs.generic_modal.close_modal();
          })
          .catch((error) => {
            this.error = generate_error_message_text(error);
          });
      });
    },

    set_initial_state() {
      this.meta = {
        name: "",
        description: "",
        color: "#007bff",
        sort_order: 0,
        is_closed_status: false,
        is_resolved_status: false,
        send_email_notification: false,
        send_sms_notification: false,
        is_active: true,
      };
      this.status_type = "open";
      this.error = "";
    },
  },
};
</script>
